import React from "react"
import { Button } from "@/components/ui/button"
import { HubCard, SegmentedPills } from "@/components/hub/hub-canvas"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { DebouncedInput } from "@/components/ui/debounced-input"
import { EditMappingDialog } from "@/components/destination-mappings/edit-mapping-dialog"
import { MappingsTable } from "@/components/destination-mappings/mappings-table"
import type { FlatMapping, FilterMode, AtsInfoState } from "@/types/ui"
import type { MappingConfig } from "@/types/fbo-api"
import { AlertTriangle, Search, X } from "lucide-react"
import { useMappingManagement } from "@/hooks/use-mapping-management"
import { useTranslation } from "@/hooks/use-translation"
import type { TranslationKey } from "@/lib/translations"

interface DestinationFieldMappingsTabProps {
  allMappings: FlatMapping[]
  hasInvalidMappings: boolean
  config: MappingConfig
  onConfigChange: (config: MappingConfig) => void
  atsInfoState: AtsInfoState
  onSave?: () => void
}

export function DestinationFieldMappingsTab({
  allMappings,
  hasInvalidMappings,
  config,
  onConfigChange,
  atsInfoState,
  onSave
}: DestinationFieldMappingsTabProps) {
  const { t, n } = useTranslation()

  // Use comprehensive hook for all logic
  const {
    searchQuery,
    setSearchQuery,
    editingMapping,
    setEditingMapping,
    expandedRows,
    filterMode,
    setFilterMode,
    filteredMappings,
    unmappedDestinationFields,
    handleMappingDialogSave,
    handleRemoveMapping,
    openMappingDialog,
    toggleRowExpansion
  } = useMappingManagement({
    allMappings,
    config,
    onConfigChange,
    onSave,
    hasInvalidMappings
  })

  const mappedCount = allMappings.filter((m) => m.isMapped).length
  const invalidCount = allMappings.filter((m) => m.isInvalid).length

  return (
    <div className="space-y-3">
      {hasInvalidMappings && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>{t("destinationFields.alerts.invalidTitle")}</AlertTitle>
          <AlertDescription>
            {t("destinationFields.alerts.invalidDescription")}
          </AlertDescription>
        </Alert>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <SegmentedPills<FilterMode>
          value={filterMode}
          onChange={setFilterMode}
          options={[
            { value: "all", label: t("destinationFields.filters.all"), count: allMappings.length },
            { value: "mapped", label: t("destinationFields.filters.mapped"), count: mappedCount },
            { value: "unmapped", label: t("destinationFields.filters.unmapped"), count: allMappings.length - mappedCount },
            ...(hasInvalidMappings ? [{ value: "invalid" as FilterMode, label: t("destinationFields.filters.invalid"), count: invalidCount, tone: "danger" as const }] : []),
          ]}
        />
        <div className="relative min-w-72 flex-1 max-w-xl">
          <Search className="absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <DebouncedInput
            placeholder={t("destinationFields.search.placeholder")}
            value={searchQuery}
            onChange={(value) => setSearchQuery(value)}
            className="h-9 rounded-[9px] border-control-border pl-9 text-[13px]"
          />
          {searchQuery && (
            <Button variant="ghost" size="icon" onClick={() => setSearchQuery("")} className="absolute right-1 top-1/2 h-6 w-6 -translate-y-1/2">
              <X className="h-3 w-3" />
              <span className="sr-only">{t("destinationFields.search.clear")}</span>
            </Button>
          )}
        </div>
        <span className="ml-auto text-xs text-muted-foreground">
          <span className="font-display text-sm font-bold text-foreground">{n(filteredMappings.length)}</span>{" "}
          {t((filteredMappings.length === 1 ? "destinationFields.count.fieldOne" : "destinationFields.count.fieldOther"))}
        </span>
      </div>

      <HubCard>
        {allMappings.length === 0 ? (
          <p className="px-4 py-8 text-center text-sm text-muted-foreground">{t("destinationFields.empty.noFields")}</p>
        ) : filteredMappings.length === 0 ? (
          <div className="px-4 py-8 text-center text-sm text-muted-foreground">
            {t("destinationFields.empty.noMatches")}
            <Button variant="ghost" size="sm" onClick={() => setSearchQuery("")} className="ml-2 h-7 text-xs">
              {t("destinationFields.search.clear")}
            </Button>
          </div>
        ) : (
          <MappingsTable
            filteredMappings={filteredMappings}
            expandedRows={expandedRows}
            onToggleRowExpansion={toggleRowExpansion}
            onEditMapping={openMappingDialog}
            onRemoveMapping={handleRemoveMapping}
            onAddMapping={openMappingDialog}
          />
        )}
      </HubCard>
      {/* Edit/Add Mapping Dialog */}
      {editingMapping && (
      <EditMappingDialog
        mapping={editingMapping}
        entities={config.entities}
        primaryEntityType={config.atsEntityType}
        unmappedDestinationFields={unmappedDestinationFields}
        atsInfoState={atsInfoState}
        onSave={handleMappingDialogSave}
        onCancel={() => setEditingMapping(null)}
      />
      )}
    </div>
  )
} 
